import React, { useEffect, useState, useContext, Suspense, lazy } from "react";
import { adminAxios } from "../../axiosInstance";
import loader from "../../assets/loader.webp";
import EditProfile from "./EditProfile";
import ChangePassword from "./ChangePassword";
// import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CurrencySetting from "./CurrencySetting";
import { fetchOnlyData } from "../../utility/adminApi";
import CategoryIcon from "@mui/icons-material/Category";
import { useQuery } from "@tanstack/react-query";
import EditReceiptTemplate from "./EditReceiptTemplate";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import LowStockLimitModal from "./LowStockLimitModal";
import { ProfileDetailsContext } from "../../context/ProfileDetailsProvider";
import { notify } from "../../components/toastify";
// import ProfileSaleStats from "./ProfileSaleStats";

// Dynamically import PaystackButton using React.lazy for React apps
const PaystackButton = lazy(() =>
  import("react-paystack").then((mod) => ({ default: mod.PaystackButton })),
);

// Small shared helper for formatting API date strings consistently
const formatDisplayDate = (dateString, fallback = "Not set") => {
  if (!dateString) return fallback;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return fallback;
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (_) {
    return fallback;
  }
};

function Profile() {
  const { profileDetails } = useContext(ProfileDetailsContext);

  const [viewModal, setViewModal] = useState(false);
  const [lowStockLimitModal, setLowStockLimitModal] = useState(false);
  const [editTab, setEditTab] = useState(false);
  const [changePasswordTab, setChangePasswordTab] = useState(false);
  const [profileData, setProfileData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [, setCurrencySymbol] = useState("");

  // For plan upgrade/renew popup/modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(""); // plan key: "pro"
  const [pendingAmount, setPendingAmount] = useState(0);
  const [paystackRef, setPaystackRef] = useState("");
  const [paystackLoading, setPaystackLoading] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);

  // Renewal: number of months selector
  const [renewMonths, setRenewMonths] = useState(1);
  const [upgradeMonths, setUpgradeMonths] = useState(1);

  // downgrade reuses selectedPlan/showUpgradeModal below, no separate state needed

  // .env
  const PAYSTACK_PUBLIC_KEY =
    "pk_live_05e106e852260673bf438c30da7cb06558b0630e";

  // React-query to get template
  const { data: template } = useQuery({
    queryKey: ["admintemplate"],
    keepPreviousData: true,
    queryFn: () => fetchOnlyData("receipt-template"),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const fetchData = async () => {
    try {
      const response = await adminAxios.get("/users/profile");
      setProfileData(response?.data?.data);
      // Profile data now includes: plan, plan_expires_at, is_plan_expired
    } catch (err) {
      console.error("Error fetching profile data", err);
      notify(
        err?.response?.data?.message || "Failed to load profile data",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Plan configs
  const planConfigs = {
    standard: {
      name: "Standard",
      amount: 5000,
    },
    pro: {
      name: "Pro",
      amount: 10000,
    },
  };

  // Only allow upgrade to pro
  const ALLOWED_UPGRADE_PLAN = "pro";
  const DOWNGRADE_PLAN = "standard";

  // Utility for renewal: get active plan or fallback
  // This function reads the plan from the up-to-date structure: profileData.plan
  const getCurrentPlan = () => {
    if (
      profileData &&
      typeof profileData.plan === "string" &&
      profileData.plan.length > 0
    ) {
      const plan = profileData.plan.toLowerCase();
      if (plan in planConfigs) return plan;
    }
    // Default/fallback if plan missing
    return "standard";
  };

  const getUserEmail = () => profileData?.email || profileDetails?.email || "";
  const getUserName = () =>
    `${profileData?.firstname || ""} ${profileData?.lastname || ""}`.trim();
  const getUserPhone = () => profileData?.phoneno || "";

  // Separate pendingAmount/effects for AI modal vs other plans
  useEffect(() => {
    if (!selectedPlan || !planConfigs[selectedPlan]) {
      return;
    }

    if (isRenewal && renewMonths) {
      const amount = planConfigs[selectedPlan].amount * renewMonths;
      setPendingAmount(amount);
      setPaystackRef(
        `Profile-Renew-${selectedPlan}-${renewMonths}M-${Date.now()}`,
      );
    } else if (!isRenewal && upgradeMonths) {
      const amount = planConfigs[selectedPlan].amount * upgradeMonths;
      setPendingAmount(amount);
      setPaystackRef(
        `Profile-Upgrade-${selectedPlan}-${upgradeMonths}M-${Date.now()}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenewal, selectedPlan, renewMonths, upgradeMonths]);

  const paystackConfig = {
    reference:
      paystackRef ||
      `Profile-${isRenewal ? "Renewal" : "Upgrade"}-${Date.now()}`,
    email: getUserEmail(),
    amount: (pendingAmount || 1000) * 100, // Paystack expects kobo
    publicKey: PAYSTACK_PUBLIC_KEY,
    currency: "NGN",
    metadata: {
      custom_fields: [
        {
          display_name: getUserName(),
          variable_name: "phone_number",
          value: getUserPhone(),
        },
      ],
      upgrade_plan: selectedPlan,
      user_id: profileData?.id,
      renewal: isRenewal ? true : false,
      renewal_months: isRenewal ? renewMonths : undefined,
      upgrade_months: !isRenewal ? upgradeMonths : undefined,
    },
  };

  // Function to call the upgrade endpoint
  const upgradePlan = async (
    amount,
    months,
    plan,
    status = "success",
    reference,
  ) => {
    try {
      const response = await adminAxios.post("billing/upgrade", {
        amount,
        months,
        plan,
        status,
        reference,
        payment_provider: "paystack",
      });
      return response.data;
    } catch (error) {
      console.error("Error upgrading plan:", error);
      throw error;
    }
  };

  // Handle the paystack payment success (shared for all plans)
  const handlePaystackSuccess = async (reference) => {
    setPaystackLoading(true);
    try {
      const months = isRenewal ? renewMonths : upgradeMonths;
      const amount = selectedPlanTotalAmount;
      const plan = selectedPlan;

      if (!plan || !amount || !months) {
        throw new Error("Missing required plan information");
      }

      // Call the upgrade/activate endpoint to update the subscription
      await upgradePlan(
        amount,
        months,
        plan,
        "success",
        reference?.reference || reference,
      );

      let planName = planConfigs[plan]?.name || "Selected";
      let msg = `Payment successful for ${planName} plan`;
      if (isRenewal)
        msg += ` (Renewal for ${renewMonths} ${renewMonths === 1 ? "month" : "months"})`;
      else
        msg += ` (${plan === DOWNGRADE_PLAN ? "Downgrade" : "Upgrade"} for ${upgradeMonths} ${upgradeMonths === 1 ? "month" : "months"})`;

      notify(`${msg}! Reference: ${reference.reference}`, "success");

      // Reset state and close modal only on success
      setShowUpgradeModal(false);
      setSelectedPlan("");
      setIsRenewal(false);
      setRenewMonths(1);
      setUpgradeMonths(1);

      // Refresh profile data to get updated plan and expiry
      await fetchData();
    } catch (err) {
      console.error("Error processing upgrade:", err);
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Payment received but processing failed. Please contact support with the payment reference.";
      notify(errorMessage, "error");
      // Keep modal open on error so user can see the error message
      // They can close it manually or we can add a retry option
    } finally {
      setPaystackLoading(false);
    }
  };

  const handlePaystackClose = () => {
    setPaystackLoading(false);
    setShowUpgradeModal(false);
    setIsRenewal(false);
    setRenewMonths(1);
    setUpgradeMonths(1);
  };

  // Handle upgrade click: always only pro available
  const onUpgradeClick = () => {
    setShowUpgradeModal(true);
    setSelectedPlan(ALLOWED_UPGRADE_PLAN); // pro only
    setPendingAmount(planConfigs[ALLOWED_UPGRADE_PLAN].amount);
    setPaystackRef(""); // will reset in useEffect
    setIsRenewal(false);
    setRenewMonths(1);
    setUpgradeMonths(1);
  };

  // Handle renew click, set plan & show modal with months select.
  const onRenewClick = () => {
    let plan = getCurrentPlan();
    setShowUpgradeModal(true);
    setSelectedPlan(plan);
    setRenewMonths(1);
    setPendingAmount(planConfigs[plan].amount);
    setPaystackRef(""); // will reset in useEffect
    setIsRenewal(true);
    setUpgradeMonths(1);
  };

  const onDowngradeClick = () => {
    setShowUpgradeModal(true);
    setSelectedPlan(DOWNGRADE_PLAN); // "standard"
    setPendingAmount(planConfigs[DOWNGRADE_PLAN].amount);
    setPaystackRef(""); // will reset in useEffect
    setIsRenewal(false);
    setRenewMonths(1);
    setUpgradeMonths(1);
  };

  // Determine current plan name from profileData
  const currentPlan = getCurrentPlan();
  const planDisplayName = planConfigs[currentPlan]?.name || "Standard";

  // Pending plan changes live at profileData.company.pending_subscriptions —
  // an array that can be missing, empty, or contain entries that aren't
  // actually "pending" (e.g. already applied/cancelled), so we guard each step.
  const pendingSubscriptions = Array.isArray(
    profileData?.company?.pending_subscriptions,
  )
    ? profileData.company.pending_subscriptions
    : [];
  const pendingDowngradeSubscription =
    pendingSubscriptions.find((sub) => sub && sub.status === "pending") || null;
  const pendingDowngradePlan = pendingDowngradeSubscription?.plan || null;
  const pendingDowngradeEffectiveDate = pendingDowngradeSubscription?.starts_at
    ? formatDisplayDate(pendingDowngradeSubscription.starts_at, null)
    : null;

  const selectedPlanAmount =
    selectedPlan && planConfigs[selectedPlan]
      ? planConfigs[selectedPlan].amount
      : 0;
  const selectedPlanMonths = isRenewal ? renewMonths : upgradeMonths;
  const selectedPlanTotalAmount = selectedPlanAmount * selectedPlanMonths;

  // Expiry date display: use plan_expires_at from API, fallback to expiry, then to default
  const expiryDisplay = formatDisplayDate(
    profileData?.plan_expires_at || profileData?.expiry,
  );

  if (isLoading) {
    return (
      <div className="w-full h-screen  flex justify-center items-center">
        <img src={loader} alt="loader" className=" w-10 lg:w-20 " />
      </div>
    );
  }

  return (
    <div className="lg:overflow-y-hidden ">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between mb-6 items-center w-full border-b-2 pb-2">
        <h3 className="text-xl font-semibold">Profile</h3>
        <div className="flex gap-6">
          <button
            onClick={() => {
              setEditTab(true);
              setChangePasswordTab(false);
            }}
            className="p-4 rounded-lg text-white bg-green-500"
          >
            Edit Profile
          </button>
          <button
            onClick={() => {
              setChangePasswordTab(true);
              setEditTab(false);
            }}
            className="p-4 rounded-lg text-white bg-blue-500"
          >
            Change Password
          </button>
        </div>
      </div>

      {editTab && (
        <EditProfile
          singleData={profileData}
          setEdittab={setEditTab}
          fetchProfile={fetchData}
          setIsLoading={setIsLoading}
          setSingleData={setProfileData}
        />
      )}

      {changePasswordTab && (
        <ChangePassword
          singleData={profileData}
          setChangePasswordTab={setChangePasswordTab}
        />
      )}

      {!editTab && (
        <div className="flex flex-col gap-8">
          {/* Subscription Summary */}
          <div className="w-full flex justify-center my-8">
            <div className="bg-transparent border border-gray-200 rounded-2xl w-full p-8 flex flex-col items-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center tracking-tight">
                Subscription Details
              </h2>

              {/* Display currently active plan and expiry below */}
              <div className="w-full flex flex-col md:flex-row gap-0 px-2 md:px-8 mt-6">
                <div className="flex-1 flex flex-col items-center justify-center">
                  <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                    Your Current Plan
                  </span>
                  <span className="bg-blue-200 text-blue-900 text-lg px-6 py-2 rounded-full font-medium border border-blue-300 shadow">
                    {planDisplayName}
                  </span>
                  {pendingDowngradePlan && (
                    <span className="mt-2 text-[11px] font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
                      Moving to{" "}
                      {planConfigs[pendingDowngradePlan]?.name ||
                        pendingDowngradePlan}{" "}
                      {pendingDowngradeEffectiveDate
                        ? `on ${pendingDowngradeEffectiveDate}`
                        : "at your next renewal"}
                    </span>
                  )}
                </div>
                <div className="flex-1 flex flex-col items-center mt-2 md:mt-0">
                  <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                    Expiry
                  </span>
                  <span className="text-base font-bold text-gray-700">
                    {expiryDisplay}
                  </span>
                </div>
              </div>
              <hr />
              {/* ======= Begin Pricing Panel Row ======= */}
              <div className="w-full flex flex-col md:flex-row gap-4 px-2 md:px-8 border-y border-gray-100">
                {/* Standard Plan */}
                <div className="flex-1 flex flex-col items-center py-7 md:border-r border-gray-100 justify-center">
                  <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                    Standard
                  </span>
                  <span className="bg-blue-100 text-blue-900 text-lg px-6 py-2 rounded-full font-medium border border-blue-200 shadow">
                    ₦{planConfigs.standard.amount.toLocaleString()}/mo
                  </span>
                  {currentPlan === "pro" ? (
                    pendingDowngradePlan === DOWNGRADE_PLAN ? (
                      <span className="mt-4 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-3 py-1 text-center">
                        Upcoming plan
                      </span>
                    ) : (
                      <button
                        className="mt-4 py-2 px-5 rounded-lg font-bold bg-gray-600 hover:bg-gray-700 text-white shadow-sm transition duration-150 text-base focus:outline-none focus:ring-2 focus:ring-gray-300"
                        style={{ letterSpacing: ".04em" }}
                        onClick={onDowngradeClick}
                      >
                        Downgrade to Standard
                      </button>
                    )
                  ) : (
                    <button
                      className="mt-4 py-2 px-5 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white shadow-sm transition duration-150 text-base focus:outline-none focus:ring-2 focus:ring-green-300"
                      style={{ letterSpacing: ".04em" }}
                      onClick={onRenewClick}
                    >
                      Renew
                    </button>
                  )}
                </div>
                {/* Pro Plan */}
                <div className="flex-1 flex flex-col items-center py-7">
                  <span className="uppercase text-xs font-semibold text-gray-500 mb-1 tracking-wider">
                    Pro
                  </span>
                  <span className="bg-indigo-100 text-indigo-900 text-lg px-6 py-2 rounded-full font-medium border border-indigo-200 shadow">
                    ₦{planConfigs.pro.amount.toLocaleString()}/mo
                  </span>
                  {currentPlan === "pro" ? (
                    // Already on Pro — show Renew button
                    <button
                      className="mt-4 py-2 px-5 rounded-lg font-bold bg-green-600 hover:bg-green-700 text-white shadow-sm transition duration-150 text-base focus:outline-none focus:ring-2 focus:ring-green-300"
                      style={{ letterSpacing: ".04em" }}
                      onClick={onRenewClick}
                    >
                      Renew
                    </button>
                  ) : (
                    // On Standard — show Upgrade button
                    <button
                      className="mt-4 py-2 px-5 rounded-lg font-bold bg-blue-700 hover:bg-blue-800 text-white shadow-sm transition duration-150 text-base focus:outline-none focus:ring-2 focus:ring-blue-400"
                      style={{ letterSpacing: ".04em" }}
                      onClick={onUpgradeClick}
                    >
                      Upgrade to Pro
                    </button>
                  )}
                </div>
              </div>
              {/* ======= End Pricing Panel Row ======= */}
            </div>
          </div>

          {/* Modal for Plan Selection (Standard/Pro) */}
          {showUpgradeModal && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60">
              <div className="bg-white rounded-lg shadow-2xl p-8 w-[95vw] max-w-md animate-fadeIn relative">
                <button
                  onClick={handlePaystackClose}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-600"
                  aria-label="Close"
                >
                  &times;
                </button>
                <h3 className="text-2xl font-bold mb-6 text-center text-blue-900">
                  {isRenewal
                    ? "Renew Your Plan"
                    : selectedPlan === ALLOWED_UPGRADE_PLAN
                      ? "Upgrade to Pro"
                      : selectedPlan === DOWNGRADE_PLAN
                        ? "Downgrade to Standard"
                        : "Change Plan"}
                </h3>
                {/* ---------- Modal Body ---------- */}
                {!isRenewal ? (
                  <div className="flex flex-col items-center my-2 gap-3 w-full">
                    <div className="flex flex-col border rounded-lg px-5 py-4 bg-indigo-50 border-blue-300 w-full">
                      <span className="font-semibold text-lg text-indigo-800">
                        {planConfigs[selectedPlan]?.name || planDisplayName}
                      </span>
                      <span className="text-xl font-bold text-gray-700">
                        ₦
                        {(
                          (selectedPlanAmount ||
                            planConfigs[selectedPlan]?.amount ||
                            0) * upgradeMonths
                        ).toLocaleString()}
                      </span>
                      <span className="uppercase mt-1 text-xs text-indigo-800 font-bold opacity-75">
                        {selectedPlan === DOWNGRADE_PLAN
                          ? "Downgrade"
                          : "Upgrade"}{" "}
                        • {upgradeMonths}{" "}
                        {upgradeMonths === 1 ? "month" : "months"}
                      </span>
                    </div>
                    {selectedPlan === DOWNGRADE_PLAN && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-center w-full">
                        You'll stay on Pro until it expires
                        {expiryDisplay !== "Not set"
                          ? ` (${expiryDisplay})`
                          : ""}
                        , then this switches you to Standard.
                      </p>
                    )}
                    <div className="w-full flex flex-col items-center">
                      <label className="text-gray-700 font-medium mb-1">
                        Select number of months to pay for:
                      </label>
                      <select
                        value={upgradeMonths}
                        className="border px-4 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 text-lg"
                        style={{ minWidth: 120 }}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          setUpgradeMonths(value);
                        }}
                      >
                        {[1, 2, 3, 6, 12].map((m) => (
                          <option value={m} key={`upgrade-${m}`}>
                            {m} {m === 1 ? "month" : "months"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center my-2 gap-3 w-full">
                    <div className="flex flex-col border rounded-lg px-5 py-4 bg-blue-50 border-blue-200 w-full">
                      <span className="font-semibold text-lg text-blue-800">
                        {planConfigs[selectedPlan]?.name || planDisplayName}
                      </span>
                      <span className="text-xl font-bold text-gray-700">
                        ₦
                        {(
                          planConfigs[selectedPlan]?.amount * renewMonths
                        ).toLocaleString()}
                      </span>
                      <span className="uppercase mt-1 text-xs text-blue-500 font-bold opacity-75">
                        Renewal &bull; {renewMonths}{" "}
                        {renewMonths === 1 ? "month" : "months"}
                      </span>
                    </div>
                    {/* Month Selector */}
                    <div className="w-full flex flex-col items-center">
                      <label
                        htmlFor="renewMonthsSelect"
                        className="text-gray-700 font-medium mb-1"
                      >
                        Select number of months to pay for:
                      </label>
                      <select
                        id="renewMonthsSelect"
                        value={renewMonths}
                        className="border px-4 py-1 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 text-lg"
                        style={{ minWidth: 120 }}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          setRenewMonths(value);
                        }}
                      >
                        {[1, 2, 3, 6, 12].map((m) => (
                          <option value={m} key={m}>
                            {m} {m === 1 ? "month" : "months"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
                <div className="mt-7">
                  {selectedPlan ? (
                    <Suspense
                      fallback={
                        <button
                          className="w-full bg-blue-500 text-white py-3 mt-2 rounded-lg"
                          disabled
                        >
                          Loading Paystack...
                        </button>
                      }
                    >
                      <PaystackButton
                        {...paystackConfig}
                        text={
                          paystackLoading
                            ? "Processing..."
                            : isRenewal
                              ? `Pay ₦${selectedPlanTotalAmount.toLocaleString()} to Renew`
                              : `Pay ₦${selectedPlanTotalAmount.toLocaleString()} to ${
                                  selectedPlan === DOWNGRADE_PLAN
                                    ? "Downgrade"
                                    : "Upgrade"
                                }`
                        }
                        className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-700 to-fuchsia-600 text-white shadow-md hover:from-fuchsia-700 hover:to-blue-800 font-bold transition-all text-lg"
                        disabled={paystackLoading || !pendingAmount}
                        onSuccess={handlePaystackSuccess}
                        onClose={handlePaystackClose}
                      />
                    </Suspense>
                  ) : (
                    <button
                      className="w-full py-3 rounded-lg font-bold bg-gray-300 text-gray-600 cursor-not-allowed"
                      disabled
                    >
                      Select a plan to continue
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Profile Details */}
          <div className="flex flex-col my-6 border-t-2">
            <h2 className="text-2xl font-bold my-4 text-gray-900">
              Profile Details
            </h2>
            <div className="w-full grid gap-4 grid-cols-1 lg:grid-cols-2">
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">First Name</span>
                <span className="text-base py-2 border border-gray-300 rounded-md pl-2 bg-gray-50">
                  {profileData?.firstname || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">Last Name</span>
                <span className="text-base py-2 border border-gray-300 rounded-md pl-2 bg-gray-50">
                  {profileData?.lastname || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">Email</span>
                <span className="text-base py-2 border border-gray-300 rounded-md pl-2 bg-gray-50">
                  {profileData?.email || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
              <div className="flex flex-col">
                <span className="font-medium text-gray-700">Phone Number</span>
                <span className="text-base py-2 border border-gray-300 rounded-md pl-2 bg-gray-50">
                  {profileData?.phoneno || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
              <div className="flex flex-col col-span-1 lg:col-span-2">
                <span className="font-medium text-gray-700">Address</span>
                <span className="text-base py-2 border border-gray-300 rounded-md pl-2 bg-gray-50">
                  {profileData?.address || (
                    <span className="text-gray-400">-</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* App Settings */}
          <div className="flex flex-col gap-4 border-t-2 pt-6">
            <h2 className="text-2xl text-red-600 font-bold text-center mb-2">
              App Settings
            </h2>
            <div className="flex flex-col flex-wrap gap-3">
              {/* Receipt Template Setting */}
              <div className="flex items-center gap-4">
                <div className="flex flex-1 items-center gap-2 py-2 px-2 border border-gray-300 rounded-md bg-gray-50">
                  <CategoryIcon className="text-xl" />
                  <span className="font-medium">Receipt Template Setting</span>
                </div>
                <button
                  onClick={() => setViewModal(true)}
                  className="py-2 px-4 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                >
                  Edit Receipt Template
                </button>
              </div>

              {/* Low Stock Limit Setting */}
              <div className="flex items-center gap-4">
                <div className="flex flex-1 items-center gap-2 py-2 px-2 border border-gray-300 rounded-md bg-gray-50">
                  <ProductionQuantityLimitsIcon className="text-xl" />
                  <span className="font-medium">Low Stock Limit Setting</span>
                </div>
                <button
                  onClick={() => setLowStockLimitModal(true)}
                  className="py-2 px-4 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-green-300"
                >
                  Edit Low Limit
                </button>
              </div>

              {/* Currency Setting */}
              <div className="flex w-full">
                <CurrencySetting setCurrencySymbol={setCurrencySymbol} />
              </div>
            </div>
            {/* Download Sales Data Consent */}
            {/* <div className="bg-gray-50 p-6 rounded shadow border mt-2">
              <h3 className="text-lg font-bold text-gray-800 mb-2">
                Monthly Data Analytics Consent
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Description: To improve reporting and business insights, we offer an option to download and process your monthly sales data.
                By enabling this option, you consent to the secure collection and analysis of transaction data for better business decisions.
              </p>
              <DownloadSalesCheckbox id={profileData?.company_id} />
            </div> */}
          </div>
        </div>
      )}
      {viewModal && (
        <EditReceiptTemplate template={template} setViewModal={setViewModal} />
      )}
      {lowStockLimitModal && (
        <LowStockLimitModal
          setLowStockLimitModal={setLowStockLimitModal}
          id={profileData?.company_id}
          fetchData={fetchData}
          profileDetails={profileDetails}
        />
      )}
    </div>
  );
}

export default Profile;
